if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.productInfo = this.closest('product-info');
        this.variantMediaMap = this.getVariantMediaMap();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.elements.viewer?.addEventListener('click', this.onViewerClick.bind(this));

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', (event) => {
            this.setActiveMedia(mediaToSwitch.dataset.target, false);
            this.handleVariantMediaSelection(event, mediaToSwitch.dataset.target);
          });
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      getVariantMediaMap() {
        const variantMediaMapElement = this.querySelector('[data-variant-media-map]');
        if (!variantMediaMapElement) return [];

        try {
          return JSON.parse(variantMediaMapElement.textContent);
        } catch (error) {
          console.error('Unable to parse product variant media map', error);
          return [];
        }
      }

      getSelectedVariant() {
        const selectedVariantElement = this.productInfo?.querySelector('variant-selects [data-selected-variant]');
        if (!selectedVariantElement) return null;

        try {
          return JSON.parse(selectedVariantElement.textContent);
        } catch (error) {
          console.error('Unable to parse selected variant', error);
          return null;
        }
      }

      getVariantForMedia(mediaId) {
        const variantCandidates = this.variantMediaMap.filter((variant) => variant.featured_media_id === mediaId);
        if (!variantCandidates.length) return null;
        if (variantCandidates.length === 1) return variantCandidates[0];

        const selectedVariant = this.getSelectedVariant();
        if (!selectedVariant?.options?.length) return variantCandidates[0];

        return variantCandidates.reduce((bestVariant, candidateVariant) => {
          const bestScore = bestVariant.options.reduce((score, optionValue, index) => {
            return score + (selectedVariant.options[index] === optionValue ? 1 : 0);
          }, 0);
          const candidateScore = candidateVariant.options.reduce((score, optionValue, index) => {
            return score + (selectedVariant.options[index] === optionValue ? 1 : 0);
          }, 0);

          return candidateScore > bestScore ? candidateVariant : bestVariant;
        });
      }

      getNumericMediaId(compoundMediaId) {
        const mediaIdMatch = `${compoundMediaId}`.match(/(\d+)$/);
        return mediaIdMatch ? Number(mediaIdMatch[1]) : null;
      }

      handleVariantMediaSelection(event, compoundMediaId) {
        const mediaId = this.getNumericMediaId(compoundMediaId);
        if (!mediaId) return;

        const variant = this.getVariantForMedia(mediaId);
        if (!variant) return;

        // Custom behavior: clicking a variant-associated media item should select the
        // matching variant instead of falling back to Dawn's modal/zoom-only behavior.
        event.preventDefault();
        event.stopPropagation();

        this.productInfo?.querySelector('variant-selects')?.selectVariantByOptions?.(variant.options);
      }

      onViewerClick(event) {
        const mediaItem = event.target.closest('[data-media-id]');
        const modalTrigger = event.target.closest('.product__modal-opener, .product__media-toggle');
        if (!mediaItem || !modalTrigger) return;

        this.handleVariantMediaSelection(event, mediaItem.dataset.mediaId);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
